/**
 * XMI (XML Metadata Interchange) Import utilities
 * Parses XMI 2.x format from various modeling tools
 */
import { logger } from './logger';

/**
 * Import XMI file and convert to internal model format
 */
export const importFromXMI = (xmiContent) => {
  try {
    logger.log('[XMI Import] Starting import, content length:', xmiContent.length);
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmiContent, 'text/xml');
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName('parsererror');
    if (parseError.length > 0) {
      logger.error('[XMI Import] XML parsing error:', parseError[0].textContent);
      throw new Error('Invalid XML format: ' + parseError[0].textContent);
    }
    
    // Find the root Model element
    const modelElement = xmlDoc.querySelector('uml\\:Model, Model');
    if (!modelElement) {
      logger.error('[XMI Import] No UML Model element found');
      throw new Error('No UML Model found in XMI file');
    }
    
    logger.log('[XMI Import] Model element found:', modelElement.getAttribute('name'));
    
    // Extract model metadata
    const modelName = modelElement.getAttribute('name') || 'Imported Model';
    const modelId = modelElement.getAttribute('xmi:id');
    
    // Extract description from comment
    let description = '';
    const commentElement = modelElement.querySelector('ownedComment, uml\\:Comment');
    if (commentElement) {
      const bodyElement = commentElement.querySelector('body');
      description = bodyElement ? bodyElement.textContent : '';
    }
    
    // Extract metadata
    const metadataElement = modelElement.querySelector('dbmodel\\:metadata, metadata');
    let modelType = 'relational';
    let globalTags = [];
    
    let modelBusinessRules = '';
    if (metadataElement) {
      const typeElement = metadataElement.querySelector('dbmodel\\:type, type');
      if (typeElement) {
        modelType = typeElement.textContent;
      }
      
      const tagsElement = metadataElement.querySelector('dbmodel\\:tags, tags');
      if (tagsElement) {
        const tagElements = tagsElement.querySelectorAll('dbmodel\\:tag, tag');
        globalTags = Array.from(tagElements).map((tag, index) => ({
          id: generateId(), // Generate ID for each global tag
          text: tag.getAttribute('name') || `Tag ${index}`,
          color: tag.getAttribute('color') || '#3b82f6'
        }));
      }

      const brElement = metadataElement.querySelector('dbmodel\\:businessRules, businessRules');
      if (brElement) {
        modelBusinessRules = brElement.textContent?.trim() || '';
      }
    }
    
    // Extract nodes (Classes/Components)
    const nodes = [];
    const packagedElements = modelElement.querySelectorAll('packagedElement');
    
    logger.log('[XMI Import] Found packagedElements:', packagedElements.length);
    
    packagedElements.forEach((element, index) => {
      try {
        const xmiType = element.getAttribute('xmi:type');
        
        if (xmiType === 'uml:Class' || xmiType === 'uml:Component' || xmiType === 'uml:Comment') {
          const node = parseNode(element);
          if (node) {
            nodes.push(node);
          }
        }
      } catch (error) {
        logger.warn(`[XMI Import] Error parsing node ${index}:`, error.message);
        // Continue with next node
      }
    });
    
    logger.log('[XMI Import] Successfully parsed nodes:', nodes.length);
    
    // Extract edges (Associations)
    const edges = [];
    packagedElements.forEach((element, index) => {
      try {
        const xmiType = element.getAttribute('xmi:type');
        
        if (xmiType === 'uml:Association') {
          const edge = parseEdge(element);
          if (edge) {
            edges.push(edge);
          }
        }
      } catch (error) {
        logger.warn(`[XMI Import] Error parsing edge ${index}:`, error.message);
        // Continue with next edge
      }
    });
    
    logger.log('[XMI Import] Successfully parsed edges:', edges.length);
    
    const result = {
      name: modelName,
      type: modelType,
      description: description,
      businessRules: modelBusinessRules,
      globalTags: globalTags,
      nodes: nodes,
      edges: edges,
      viewport: { x: 0, y: 0, zoom: 1 }
    };
    
    logger.log('[XMI Import] Import successful:', {
      name: result.name,
      nodes: result.nodes.length,
      edges: result.edges.length,
      globalTags: result.globalTags.length
    });
    
    return result;
  } catch (error) {
    logger.error('[XMI Import] Error during import:', error);
    logger.error('[XMI Import] Error stack:', error.stack);
    throw new Error(`Failed to import XMI: ${error.message}`);
  }
};

/**
 * Parse a node (entity) from XMI
 */
function parseNode(element) {
  const rawId = element.getAttribute('xmi:id');
  // Sanitize ID to ensure MongoDB compatibility (no special chars that might cause issues)
  const id = rawId ? sanitizeId(rawId) : generateId();
  const name = element.getAttribute('name') || 'Unnamed';
  const xmiType = element.getAttribute('xmi:type');
  
  // Determine node type from stereotype or XMI type
  let nodeType = 'table';
  const stereotypeElement = element.querySelector('dbmodel\\:stereotype, stereotype');
  if (stereotypeElement) {
    nodeType = stereotypeElement.textContent;
  } else if (xmiType === 'uml:Component') {
    nodeType = 'view';
  } else if (xmiType === 'uml:Comment') {
    nodeType = 'text';
  }
  
  // Extract description
  let description = '';
  const commentElement = element.querySelector('ownedComment');
  if (commentElement) {
    const bodyElement = commentElement.querySelector('body');
    description = bodyElement ? bodyElement.textContent : '';
  }
  
  // Extract position
  let position = { x: 100, y: 100 };
  const positionElement = element.querySelector('dbmodel\\:position, position');
  if (positionElement) {
    const x = parseFloat(positionElement.getAttribute('x'));
    const y = parseFloat(positionElement.getAttribute('y'));
    position = {
      x: isNaN(x) ? 100 : x,
      y: isNaN(y) ? 100 : y
    };
  }
  
  // Extract dimensions
  let width = 250;
  let height = 200;
  const dimensionsElement = element.querySelector('dbmodel\\:dimensions, dimensions');
  if (dimensionsElement) {
    width = parseFloat(dimensionsElement.getAttribute('width')) || 250;
    height = parseFloat(dimensionsElement.getAttribute('height')) || 200;
  }
  
  // Extract color
  let color = null;
  const colorElement = element.querySelector('dbmodel\\:color, color');
  if (colorElement) {
    color = colorElement.textContent;
  }
  
  // Extract schema
  let schema = '';
  const schemaElement = element.querySelector('dbmodel\\:schema, schema');
  if (schemaElement) {
    schema = schemaElement.textContent?.trim() || '';
  }

  // Extract tags
  let tags = [];
  const tagsElement = element.querySelector('dbmodel\\:tags, tags');
  if (tagsElement) {
    const tagElements = tagsElement.querySelectorAll('dbmodel\\:tag, tag');
    tags = Array.from(tagElements).map((tag, index) => ({
      id: generateId(), // Generate ID for each tag
      text: tag.getAttribute('name') || `Tag ${index}`,
      color: tag.getAttribute('color') || '#3b82f6'
    }));
  }
  
  // Extract SQL definition for views
  let sqlDefinition = '';
  const sqlDefElement = element.querySelector('dbmodel\\:sqlDefinition, sqlDefinition');
  if (sqlDefElement) {
    sqlDefinition = sqlDefElement.textContent;
  }
  
  // Extract text content for text nodes
  let textContent = '';
  const textContentElement = element.querySelector('dbmodel\\:textContent, textContent');
  if (textContentElement) {
    textContent = textContentElement.textContent;
  }
  
  // Extract fields (attributes)
  const fields = [];
  const attributes = element.querySelectorAll('ownedAttribute');
  attributes.forEach(attr => {
    try {
      const field = parseField(attr);
      if (field) {
        fields.push(field);
      }
    } catch (error) {
      logger.warn('[XMI Import] Error parsing field:', error.message);
      // Continue with next field
    }
  });
  
  // Extract indexes
  const indexes = [];
  const indexesElement = element.querySelector('dbmodel\\:indexes, indexes');
  if (indexesElement) {
    const indexElements = indexesElement.querySelectorAll('dbmodel\\:index, index');
    indexElements.forEach(idxElement => {
      try {
        const index = parseIndex(idxElement);
        if (index) {
          indexes.push(index);
        }
      } catch (error) {
        logger.warn('[XMI Import] Error parsing index:', error.message);
      }
    });
  }
  
  // Extract materialized flag for views
  let materialized = false;
  const materializedElement = element.querySelector('dbmodel\\:materialized, materialized');
  if (materializedElement && materializedElement.textContent === 'true') {
    materialized = true;
  }
  
  // Extract partitioning
  let partitioning = null;
  const partitioningElement = element.querySelector('dbmodel\\:partitioning, partitioning');
  if (partitioningElement) {
    try {
      partitioning = JSON.parse(partitioningElement.textContent.trim());
    } catch (e) {
      logger.warn('[XMI Import] Error parsing partitioning:', e.message);
    }
  }

  const node = {
    id: id,
    type: nodeType,
    position: position,
    data: {
      name: name || 'Unnamed',
      description: description || '',
      fields: fields || [],
      indexes: indexes || [],
      partitioning: partitioning || { type: 'none' },
      color: color,
      tags: tags || []
    },
    width: width,
    height: height,
    measured: { width: width, height: height }
  };
  
  // Add type-specific data
  if (nodeType === 'view' && sqlDefinition) {
    node.data.sqlDefinition = sqlDefinition;
  }
  
  if (nodeType === 'view' && materialized) {
    node.data.materialized = true;
  }
  
  if (nodeType === 'text' && textContent) {
    node.data.content = textContent;
  }
  
  // Ensure text nodes have content field
  if (nodeType === 'text' && !node.data.content) {
    node.data.content = '';
  }
  
  return node;
}

/**
 * Parse a field (attribute) from XMI
 */
function parseField(element) {
  const rawId = element.getAttribute('xmi:id');
  const id = rawId ? sanitizeId(rawId) : generateId();
  const name = element.getAttribute('name') || 'unnamed';
  
  // Extract type — if a UDT label is present, it takes precedence over the resolved base type
  let type = 'VARCHAR';
  const udtElement = element.querySelector('dbmodel\\:userDefinedType, userDefinedType');
  if (udtElement && udtElement.textContent?.startsWith('UDT:')) {
    type = udtElement.textContent.trim();
  } else {
    const typeElement = element.querySelector('type');
    if (typeElement) {
      const href = typeElement.getAttribute('href');
      if (href) type = href;
    }
  }
  
  // Extract description
  let description = '';
  const commentElement = element.querySelector('ownedComment');
  if (commentElement) {
    const bodyElement = commentElement.querySelector('body');
    description = bodyElement ? bodyElement.textContent : '';
  }
  
  // Extract constraints
  let isPrimaryKey = false;
  let isForeignKey = false;
  let nullable = true;
  let unique = false;
  let autoIncrement = false;
  let defaultValue = undefined;
  
  const constraintsElement = element.querySelector('dbmodel\\:constraints, constraints');
  if (constraintsElement) {
    const pkElement = constraintsElement.querySelector('dbmodel\\:primaryKey, primaryKey');
    if (pkElement) {
      isPrimaryKey = pkElement.textContent === 'true';
    }
    
    const fkElement = constraintsElement.querySelector('dbmodel\\:foreignKey, foreignKey');
    if (fkElement) {
      isForeignKey = fkElement.textContent === 'true';
    }
    
    const nullableElement = constraintsElement.querySelector('dbmodel\\:nullable, nullable');
    if (nullableElement) {
      nullable = nullableElement.textContent === 'true';
    }
    
    const uniqueElement = constraintsElement.querySelector('dbmodel\\:unique, unique');
    if (uniqueElement) {
      unique = uniqueElement.textContent === 'true';
    }
    
    const autoIncrementElement = constraintsElement.querySelector('dbmodel\\:autoIncrement, autoIncrement');
    if (autoIncrementElement) {
      autoIncrement = autoIncrementElement.textContent === 'true';
    }
    
    const defaultElement = constraintsElement.querySelector('dbmodel\\:defaultValue, defaultValue');
    if (defaultElement) {
      defaultValue = defaultElement.textContent;
    }

    const checkConstraintElement = constraintsElement.querySelector('dbmodel\\:checkConstraint, checkConstraint');
    if (checkConstraintElement) {
      var checkConstraint = checkConstraintElement.textContent;
    }
  }
  
  // Ensure field object has all required properties
  const field = {
    id: id,
    name: name || 'unnamed',
    type: type || 'VARCHAR',
    isPrimaryKey: isPrimaryKey || false,
    isForeignKey: isForeignKey || false,
    nullable: nullable !== undefined ? nullable : true
  };
  
  // Add optional properties only if they have values
  if (description) {
    field.description = description;
  }
  
  if (unique) {
    field.unique = unique;
  }
  
  if (autoIncrement) {
    field.autoIncrement = autoIncrement;
  }
  
  if (defaultValue !== undefined && defaultValue !== null) {
    field.defaultValue = defaultValue;
  }

  if (typeof checkConstraint !== 'undefined' && checkConstraint) {
    field.checkConstraint = checkConstraint;
  }
  
  return field;
}

/**
 * Parse an index from XMI
 */
function parseIndex(element) {
  const id = element.getAttribute('id') || generateId();
  const name = element.getAttribute('name') || 'unnamed_index';
  const type = element.getAttribute('type') || 'Non-Clustered';
  
  // Extract columns
  const columns = [];
  const columnsElement = element.querySelector('dbmodel\\:columns, columns');
  if (columnsElement) {
    const columnElements = columnsElement.querySelectorAll('dbmodel\\:column, column');
    columnElements.forEach(colElement => {
      columns.push({
        fieldName: colElement.getAttribute('fieldName') || '',
        order: colElement.getAttribute('order') || 'ASC'
      });
    });
  }
  
  return {
    id: id,
    name: name,
    type: type,
    columns: columns
  };
}

/**
 * Parse an edge (association) from XMI
 */
function parseEdge(element) {
  const rawId = element.getAttribute('xmi:id');
  const id = rawId ? sanitizeId(rawId) : generateId();
  const label = element.getAttribute('name') || '';
  
  // Extract source and target from memberEnd
  const memberEnds = element.querySelectorAll('memberEnd');
  if (memberEnds.length < 2) {
    logger.warn('[XMI Import] Association missing memberEnd elements');
    return null;
  }
  
  const rawSource = memberEnds[0].getAttribute('xmi:idref');
  const rawTarget = memberEnds[1].getAttribute('xmi:idref');
  
  if (!rawSource || !rawTarget) {
    logger.warn('[XMI Import] Association missing source or target reference');
    return null;
  }
  
  // Sanitize source and target IDs to match sanitized node IDs
  const source = sanitizeId(rawSource);
  const target = sanitizeId(rawTarget);
  
  // Extract multiplicity from ownedEnd elements
  const ownedEnds = element.querySelectorAll('ownedEnd');
  let sourceMultiplicity = '1';
  let targetMultiplicity = 'N';
  
  if (ownedEnds.length >= 2) {
    // First ownedEnd is source
    const sourceLower = ownedEnds[0].querySelector('lowerValue');
    const sourceUpper = ownedEnds[0].querySelector('upperValue');
    sourceMultiplicity = getCardinalityFromMultiplicity(
      sourceLower ? sourceLower.getAttribute('value') : '1',
      sourceUpper ? sourceUpper.getAttribute('value') : '1'
    );
    
    // Second ownedEnd is target
    const targetLower = ownedEnds[1].querySelector('lowerValue');
    const targetUpper = ownedEnds[1].querySelector('upperValue');
    targetMultiplicity = getCardinalityFromMultiplicity(
      targetLower ? targetLower.getAttribute('value') : '0',
      targetUpper ? targetUpper.getAttribute('value') : '*'
    );
  }
  
  const cardinality = `${sourceMultiplicity}:${targetMultiplicity}`;
  
  // Extract relationship metadata
  let edgeType = 'default';
  let cardinalityLocked = true; // Default to locked for imported relationships
  let sourceField = undefined;
  let targetField = undefined;
  let targetHandle = undefined;
  
  const relationshipElement = element.querySelector('dbmodel\\:relationship, relationship');
  if (relationshipElement) {
    const typeElement = relationshipElement.querySelector('dbmodel\\:type, type');
    if (typeElement) {
      edgeType = typeElement.textContent;
    }
    
    const cardElement = relationshipElement.querySelector('dbmodel\\:cardinality, cardinality');
    if (cardElement) {
      // Use stored cardinality if available (more accurate)
      const storedCardinality = cardElement.textContent;
      if (storedCardinality) {
        // Override extracted cardinality
      }
    }
    
    const lockedElement = relationshipElement.querySelector('dbmodel\\:cardinalityLocked, cardinalityLocked');
    if (lockedElement) {
      cardinalityLocked = lockedElement.textContent === 'true';
    }
    
    const sourceFieldElement = relationshipElement.querySelector('dbmodel\\:sourceField, sourceField');
    if (sourceFieldElement) {
      sourceField = sourceFieldElement.textContent;
    }
    
    const targetFieldElement = relationshipElement.querySelector('dbmodel\\:targetField, targetField');
    if (targetFieldElement) {
      targetField = targetFieldElement.textContent;
    }
    
    const targetHandleElement = relationshipElement.querySelector('dbmodel\\:targetHandle, targetHandle');
    if (targetHandleElement) {
      targetHandle = targetHandleElement.textContent;
    }
  }
  
  const edge = {
    id: id,
    source: source,
    target: target,
    type: edgeType,
    data: {
      label: label,
      cardinality: cardinality,
      cardinalityLocked: cardinalityLocked
    }
  };
  
  if (sourceField) {
    edge.data.sourceField = sourceField;
  }
  
  if (targetField) {
    edge.data.targetField = targetField;
  }
  
  if (targetHandle) {
    edge.targetHandle = targetHandle;
  }
  
  return edge;
}

/**
 * Convert UML multiplicity to cardinality notation
 */
function getCardinalityFromMultiplicity(lower, upper) {
  if (lower === '1' && upper === '1') {
    return '1';
  }
  if ((lower === '0' || lower === '1') && (upper === '*' || upper === 'unlimited' || upper === '-1')) {
    return 'N';
  }
  return '1';
}

/**
 * Generate unique ID
 */
function generateId() {
  return Date.now() + '-' + Math.random().toString(36).substr(2, 9);
}

/**
 * Sanitize ID to ensure it's safe for MongoDB and doesn't have problematic characters
 */
function sanitizeId(id) {
  if (!id) return generateId();
  // Keep alphanumeric, hyphens, underscores - replace others with hyphen
  return id.replace(/[^a-zA-Z0-9_-]/g, '-');
}

/**
 * Validate XMI content
 */
export const validateXMI = (content) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(content, 'text/xml');
    
    const parseError = xmlDoc.getElementsByTagName('parsererror');
    if (parseError.length > 0) {
      return false;
    }
    
    const modelElement = xmlDoc.querySelector('uml\\:Model, Model, xmi\\:XMI');
    return !!modelElement;
  } catch (error) {
    return false;
  }
};
